<template>
  <v-dialog v-model="show" max-width="700px" persistent scrollable>
    <v-card style="max-height: 90vh; display: flex; flex-direction: column;">
      <validation-observer ref="form" v-slot="observer" style="display: flex; flex-direction: column; flex: 1; overflow: hidden;">
        <v-card-text v-if="formValues" style="overflow-y: auto; flex: 1;">
          <v-card-title mt-2 style="justify-content: center;">{{ modalTitle }}</v-card-title>

          <v-form>
            <v-col cols="12">
              <!-- Payment method name field — always visible above tabs -->
              <validation-provider
                v-slot="{ errors }"
                :name="$t('settings.paymentMethod.paymentName')"
                rules="required|label_name"
              >
                <v-text-field
                  v-model="localeName"
                  :label="$t('settings.paymentMethod.paymentName')"
                  :error-messages="errors"
                  clearable
                />
              </validation-provider>

              <v-tabs v-model="activeTab" grow>
                <v-tab>{{ $t('settings.paymentMethod.general') }}</v-tab>
                <v-tab>{{ $t('settings.paymentMethod.translations') }}</v-tab>
              </v-tabs>

              <v-tabs-items v-model="activeTab">
                <!-- Général tab -->
                <v-tab-item>
                  <v-row class="mt-4">
                    <v-col cols="12">
                      <div style="width: fit-content;">
                        <v-switch
                          v-model="formValues.activate"
                          :label="$t('settings.active').toUpperCase()"
                          class="ml-4"
                        />
                      </div>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <validation-provider v-slot="{ errors }" :name="$t('settings.paymentMethod.methodId')" rules="identifier">
                        <v-text-field
                          v-model="formValues.method_id"
                          :label="$t('settings.paymentMethod.methodId')"
                          :error-messages="errors"
                          dense
                          clearable
                        />
                      </validation-provider>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <validation-provider v-slot="{ errors }" :name="$t('settings.paymentMethod.erpMethodId')" rules="identifier">
                        <v-text-field
                          v-model="formValues.erp_method_id"
                          :label="$t('settings.paymentMethod.erpMethodId')"
                          :error-messages="errors"
                          dense
                          clearable
                        />
                      </validation-provider>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('settings.paymentMethod.maxQuantity')"
                        rules="integer|min_value:1"
                      >
                        <v-text-field
                          v-model="formValues.max_quantity"
                          :label="$t('settings.paymentMethod.maxQuantity')"
                          :persistent-hint="true"
                          :hint="$t('settings.paymentMethod.maxQuantityHint')"
                          outlined
                          dense
                          clearable
                          :error-messages="errors"
                        />
                      </validation-provider>
                    </v-col>

                    <v-col cols="12" sm="6">
                      <validation-provider
                        v-slot="{ errors }"
                        :name="$t('settings.paymentMethod.maxAmount')"
                        rules="double|min_value:0.01"
                      >
                        <v-text-field
                          v-model="formValues.max_amount"
                          :label="$t('settings.paymentMethod.maxAmount')"
                          :persistent-hint="true"
                          :hint="$t('settings.paymentMethod.maxAmountHint')"
                          outlined
                          dense
                          clearable
                          :error-messages="errors"
                        />
                      </validation-provider>
                    </v-col>

                    <v-col cols="12">
                      <v-select
                        v-model="formValues.allowed_roles"
                        :items="availableRoles"
                        :label="$t('settings.paymentMethod.allowedRolesLabel')"
                        multiple
                        dense
                      />
                    </v-col>
                  </v-row>
                </v-tab-item>

                <!-- Translations tab -->
                <v-tab-item>
                  <div v-for="lang in sortedLanguages" :key="lang.code" class="d-flex align-center mb-3 mt-5">
                    <div class="lang-label mr-3">
                      <div class="flag-icon mr-2" :class="'flag-icon-' + lang.flag"></div>
                      <span class="text-uppercase font-weight-medium">{{ lang.code }}</span>
                    </div>
                    <validation-provider
                      v-slot="{ errors }"
                      :name="$t('settings.paymentMethod.translation') + ' ' + lang.code.toUpperCase()"
                      rules="label_name"
                      class="flex-grow-1"
                    >
                      <v-text-field
                        v-model="formValues.labels[lang.code]"
                        :label="getTranslationLabel(lang.code)"
                        :error-messages="lang.code === 'en' ? (englishTranslationError || errors[0]) : errors[0]"
                        outlined
                        dense
                        hide-details="auto"
                        @input="onTranslationInput(lang.code)"
                      />
                    </validation-provider>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </v-col>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-0 mt-2">
          <v-btn
            color="primary"
            style="width: 50%"
            tile
            @click="onSubmit"
            :disabled="observer.invalid"
          >
            {{ $t('settings.alerts.validate') }}
          </v-btn>
          <v-btn
            class="ml-0"
            color="secondary"
            style="width: 50%"
            tile
            @click="close"
          >
            {{ $t('settings.alerts.cancel') }}
          </v-btn>
        </v-card-actions>
      </validation-observer>
    </v-card>
  </v-dialog>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { sortLanguages } from '@/helpers/helper'
import { SUPPORTED_LANGUAGES } from '@/constants/languages'

const DEFAULT_ROLES = ['oms-admin', 'oms-customer-service', 'oms-shop-manager', 'default-user']

export default {
  name: 'PaymentMethodFormDialog',
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    formValue: {
      type: Object,
      default: undefined,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 0,
      formValues: null,
      englishTranslationError: '',
    }
  },
  mounted() {
    const emptyLabels = Object.fromEntries(SUPPORTED_LANGUAGES.map((lang) => [lang.code, '']))

    if (this.formValue) {
      this.formValues = {
        ...JSON.parse(JSON.stringify(this.formValue)),
        labels: { ...emptyLabels, ...this.formValue.labels },
        allowed_roles: this.formValue.allowed_roles || [...DEFAULT_ROLES],
      }
    } else {
      this.formValues = {
        activate: false,
        max_amount: null,
        max_quantity: null,
        method_id: null,
        erp_method_id: null,
        labels: emptyLabels,
        allowed_roles: [...DEFAULT_ROLES],
      }
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    localeName: {
      get() {
        return this.formValues?.labels?.[this.$i18n.locale] || ''
      },
      set(value) {
        if (this.formValues) {
          this.$set(this.formValues.labels, this.$i18n.locale, value)
        }
      },
    },
    modalTitle() {
      return this.formValue
        ? this.$t('settings.paymentMethod.editPaymentMethod')
        : this.$t('settings.paymentMethod.addPaymentMethod')
    },
    sortedLanguages() {
      return sortLanguages(SUPPORTED_LANGUAGES)
    },
    availableRoles() {
      return [
        { text: this.$t('users.seller'), value: 'default-user' },
        { text: this.$t('users.shopManager'), value: 'oms-shop-manager' },
        { text: this.$t('users.customerService'), value: 'oms-customer-service' },
        { text: this.$t('users.admin'), value: 'oms-admin' },
      ]
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
    getTranslationLabel(langCode) {
      const required = langCode === 'en' ? ' *' : ''
      return `${this.$t('settings.paymentMethod.translation')} ${langCode.toUpperCase()}${required}`
    },
    onTranslationInput(langCode) {
      if (langCode === 'en') {
        this.englishTranslationError = ''
      }
    },
    validateCustomRules() {
      this.englishTranslationError = ''

      if (!this.formValues.labels?.en?.trim()) {
        this.activeTab = 1
        this.englishTranslationError = this.$t('settings.paymentMethod.englishRequired')
        return false
      }

      return true
    },
    async onSubmit() {
      if (!this.validateCustomRules()) {
        return
      }

      const success = await this.$refs.form.validate()
      if (success) {
        const result = {
          activate: this.formValues.activate ?? false,
          labels: this.filterEmptyLabels(this.formValues.labels),
          max_amount: this.formValues.max_amount != null && this.formValues.max_amount !== '' ? parseFloat(this.formValues.max_amount) : undefined,
          max_quantity: this.formValues.max_quantity != null && this.formValues.max_quantity !== '' ? parseInt(this.formValues.max_quantity, 10) : undefined,
          method_id: this.formValues.method_id || undefined,
          erp_method_id: this.formValues.erp_method_id || undefined,
          allowed_roles: this.formValues.allowed_roles,
        }
        this.$emit('validateForm', result)
      }
    },
    filterEmptyLabels(labels) {
      const filtered = {}
      Object.keys(labels || {}).forEach((key) => {
        const val = labels[key]
        if (typeof val === 'string' && val.trim() !== '') {
          filtered[key] = val.trim()
        }
      })
      return filtered
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/flags.scss';

.lang-label {
  min-width: 70px;
  display: flex;
  align-items: center;
}
</style>
